import React, { FC, useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import cx from 'classnames';
import Link from 'next/link';
import PriceBadge from 'components/Others/PriceBadge';
import ProductTag from 'components/Others/ProductTag';
import UIButton from 'components/Others/UIButton';
import { LENS_COLOR_MAP } from 'components/VTO/VTO.constants'
import { useHover } from "@uidotdev/usehooks";
import 'swiper/css';
import 'swiper/css/pagination';
import {
  generateGlassesThumbImage,
  generateSparePartThumbImage,
  generateAccessoriesThumbImage
} from 'utils/image.utils';
import * as ga from '../../lib/ga';
import { TAGGED_PRODUCTS } from './ProductTags';
import ProductSpecificIcon from 'components/Others/ProductSpecificIcon';
import { useRouter } from 'next/router';
import LensColorSelector from 'components/ProductDetail/LensColorSelector';
import LoadingImage from 'components/LoadingImage/LoadingImage';
import ProductCardMobileSwiper from './ProductCardMobileSwiper';
import { imageLoader } from 'functions/imageLoader';
import { useVTOStore } from 'zus/vtoStore';
import { CategoryLensTypes, FrameShape } from 'constants/dataTypes';
import { getMinPriceForLensType } from 'functions/filterLensTypes';
import { frameShapeToName } from 'functions/frameShapeToName';

const DEFAULT_GLASSES_IMAGE_ORDER = [0, 1];

interface ProductCardProps {
  data: {
    images: string[];
    href: string;
    title: string;
    slug?: string;
    price: any;
    prio: boolean;
    regularPrice?: number;
    additionalTags?: string[];
    productTags?: string[];
    discountTag?: boolean;
    colorSlug?: string;
    armColorSlug?: string;
    categorySlug?: string;
    shape?: FrameShape;
  };
  width: number;
  onClickPoroduct?: () => void;
  categoryLensTypes?: CategoryLensTypes[];
  vtoEnabled?: boolean;
  toggleVTO?: (color: any, armColorSlug: string, shape: FrameShape) => void;
  initialLensColor?: string;
}

//write function to return color map of initialLensColor
const getColorMap = (initialLensColor: string) => {
  return LENS_COLOR_MAP.find((color) => color.id === initialLensColor);
}

function ProductCard({ data, width, onClickPoroduct, vtoEnabled, toggleVTO, categoryLensTypes, initialLensColor }: ProductCardProps) {
  const [hoverRef, isImageHover] = useHover();
  const [imageHover, isHover] = useHover();
  const [selectedLensColor, setSelectedLensColor] = useState<any>(initialLensColor ? getColorMap(initialLensColor) : null);
  const router = useRouter();

  const tempArr = router.asPath.split('?')?.[0].split('/');
  const isShopPage = tempArr[tempArr.length - 1] === 'shop' || tempArr[tempArr.length - 1] === 'dcatalogue';

  const isSunglassess = useMemo(() => {
    return router.asPath.indexOf('sunglasses') > -1 || data.href.indexOf('sunglasses') > -1;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.asPath]);

  // Define the filtered colors for lens color selector
  const filteredLensColors = useMemo(() => {
    return LENS_COLOR_MAP
      .filter((color) => color.id !== 'clear')
      .filter((color: any) =>
        !color?.disabledRegions?.includes(router.locale as string) &&
        !color?.disabledCategories?.includes(router.query.category as string) &&
        !(color?.id == 'clear')
      )
      .filter((color: any) =>
        !((color?.disabledConditions?.[0]?.category === router.query.category ||
          data.href.indexOf(color?.disabledConditions?.[0]?.category) > -1) &&
          color?.disabledConditions?.[0]?.locale === router.locale)
      )
      // Only show mirror colors if categorySlug contains sun-glasses
      .filter((color: any) => {
        const isMirrorColor = color.id.includes('mirror-');
        const isSunGlassesCategorySlug = data.categorySlug?.includes('sun-glasses') || data.categorySlug?.includes('sunglasses');

        // If it's a mirror color, only include it when categorySlug has sun-glasses
        // If it's not a mirror color, include it regardless
        return !isMirrorColor || (isMirrorColor && isSunGlassesCategorySlug);
      });
  }, [router.locale, router.query.category, data.href, data.categorySlug]);

  const tag = useMemo(() => {
    return TAGGED_PRODUCTS.find((tag) => tag.paths.includes(data.href) && tag.availableRegions.includes(router.locale as any));
  }, [data.href, router.locale]);

  const hasModelPhoto = useMemo(() => {
    if (data.href.includes('glasses') || data.href.includes('frames')) {
      return true;
    }
    return false;
  }, [data.href]);

  const cardImages: false | string[] = useMemo(() => {
    if (data.href.includes('spare-parts')) {
      return [generateSparePartThumbImage(data.title)];
    }

    if (data.href.includes('accessories')) {
      return [generateAccessoriesThumbImage(data.slug || data.title)];
    }

    if (data.href.includes('contact-lens')) {
      return data.images && data.images.length > 0 && data.images.map((image) => image.includes('contact-lenses/v2/') ? image : image.replace('contact-lenses/', 'contact-lenses/v2/'));
    }
    const defaultLensColor = (data.href.includes('sunglasses')
      ? selectedLensColor?.id || 'grey'
      : 'clear');

    return DEFAULT_GLASSES_IMAGE_ORDER.map((index: any) =>
      generateGlassesThumbImage(data.colorSlug || data.title, data.armColorSlug || data.title, index, defaultLensColor, data.shape)
    );
  }, [data.href, data.title, selectedLensColor, router, initialLensColor, data.shape]);

  useEffect(() => {
    const defaultColor = initialLensColor || ((router.query.lensColor || router.query.lenscolor) || 'grey');
    setSelectedLensColor(() => getColorMap(defaultColor as string));
  }, [router.query.lensColor, router.query.lenscolor, initialLensColor]);

  const url = useMemo(() => {

    let targetUrl = data.href;

    let queryParams = [];
    if (selectedLensColor && isSunglassess && selectedLensColor.id !== 'grey') {
      queryParams.push(`lensColor=${selectedLensColor.id}`);
    }
    if (data.armColorSlug && data.armColorSlug !== data.colorSlug) {
      queryParams.push(`armColor=${data.armColorSlug}`);
    }
    if (data.shape && data.shape !== 'classic') {
      queryParams.push(`shape=${data.shape}`);
    }

    if (queryParams.length > 0) {
      targetUrl = `${data.href}?${queryParams.join('&')}`;
    }

    return targetUrl;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.href, selectedLensColor, router])

  const calculatedPrice = useMemo(() => {
    if (categoryLensTypes) {
      const mirrorLensPrice = getMinPriceForLensType(categoryLensTypes, 'mirror-tint');
      if (selectedLensColor?.id.includes('mirror-')) {
        return mirrorLensPrice?.price;
      }
      else if (categoryLensTypes.length > 0 && data.categorySlug?.includes('-')) {
        const categoryLensType = categoryLensTypes.find((item) => item.type === data.categorySlug);
        if (categoryLensType) {
          return categoryLensType?.price;
        }
      }
      return data.price;
    }
    return data.price
  }, [selectedLensColor, data.price, categoryLensTypes])

  const calculatedRegularPrice = useMemo(() => {
    if (categoryLensTypes) {
      const mirrorLensPrice = getMinPriceForLensType(categoryLensTypes, 'mirror-tint');
      if (selectedLensColor?.id.includes('mirror-')) {
        return mirrorLensPrice?.regular_price;
      }
      else if (categoryLensTypes.length > 0 && data.categorySlug?.includes('-')) {
        const categoryLensType = categoryLensTypes.find((item) => item.type === data.categorySlug);
        if (categoryLensType) {
          return categoryLensType?.regular_price;
        }
      }
      return data.regularPrice;
    }
    return data.regularPrice
  }, [selectedLensColor, data.regularPrice, categoryLensTypes])


  const filtredProductTag = useMemo(() => {
    return data.productTags?.filter((item) => item.toLocaleUpperCase() !== 'XR');
  }, [data.productTags]);

  const handleLensColorHover = (color: any) => {
    setSelectedLensColor(color);
  };

  const isImageContain = (product: any) => {
    return product?.slug?.includes('flex-accessory-clip') || product?.slug?.includes('spex-glasses');
  }

  const getCardHeight = useMemo(() => {
    if (isSunglassess) {
      return 'h-[290px]';
    }

    if (router.asPath.includes('glasses')) {
      return 'h-[340px]';
    }

    return 'h-[360px]';
  }, [router.asPath, isSunglassess]);


  return (
    <Link
      prefetch={false}
      href={url}
      onClick={() => {
        if (onClickPoroduct) {
          onClickPoroduct();
        }
      }}
      className="relative text-left group bg-white overflow-hidden duration-300">
      {!!tag && data.shape !== 'round' && <ProductTag {...tag} />}
      <div className="flex flex-col items-end text-right right-[10px] top-[10px] absolute z-10">
        {((data.price != data.regularPrice) && data.discountTag && data.regularPrice) && <div className="mb-[5px] text-center w-fit">
          <ProductTag isSmall={true} text={`
          ${Math.round((1 - data?.price / data?.regularPrice) * 100)}% Discount
        `} isAbsolute={false} textColor="#fff" backgroundColor="#47AC40" />
        </div>
        }
        {(data.additionalTags && (filtredProductTag?.length == 0)) &&
          data.additionalTags.map((item, idx) => (
            <div key={idx} className="mb-[5px] text-center w-fit">
              <ProductTag isSmall={true} text={item} isAbsolute={false} textColor="#fff" backgroundColor="#FF8200" />
            </div>
          ))
        }
        {data.shape && data.shape.includes('round') && (
          <div className="mb-[5px] text-center w-fit">
            <ProductTag isSmall={true} text="New Shape" isAbsolute={false} textColor="#fff" backgroundColor="#FF8200" />
          </div>
        )}
        {filtredProductTag &&
          filtredProductTag.map((item, idx) => (
            <div key={idx} className="mb-[5px] first-of-type:mt-[5px] z-10 text-center w-fit">
              <ProductTag isSmall={true} text={item} isAbsolute={false} textColor="#fff" backgroundColor="#FF8200" />
            </div>
          ))}
      </div>
      <ProductSpecificIcon productTitle={data.title} />

      {/* Main card content */}
      <div className="flex flex-col h-full">
        {/* Image container with fixed height */}
        <div className="relative w-full bg-[#f6f8fa]" ref={hoverRef as any}>
          {width >= 1140 ? (
            <div className={cx(
              "w-full",
              getCardHeight
            )} ref={imageHover as any}>
              {cardImages && cardImages.length > 1 && cardImages[1] && (
                <div className={cx(
                  "relative h-full w-full animate-opacityinquick",
                  isImageHover ? 'block' : 'hidden'
                )}>
                  <LoadingImage
                    style={{
                      objectFit: "cover",
                      width: "100%",
                      height: "100%",
                    }}
                    sizes="(max-width: 768px) 100vw, 414px"
                    src={cardImages[1]}
                    alt={data.title}
                  />
                </div>
              )}
              <div className={cx(
                "relative h-full w-full",
                cardImages && cardImages.length > 1 && cardImages[1] && isImageHover ? 'hidden' : 'block'
              )}>
                {cardImages && (cardImages.length === 1 ? (
                  <Image
                    quality={89}
                    className={cx(
                      { 'scale-90': (router.asPath.indexOf('accessories') > -1 || isShopPage) },
                      'w-full h-full'
                    )}
                    key={cardImages[0]}
                    fill
                    style={{
                      objectFit: isImageContain(data) ? 'contain' : 'cover',
                      width: "100%",
                      height: "100%",
                    }}
                    sizes="(max-width: 768px) 100vw, 414px"
                    loader={imageLoader}
                    src={cardImages[0]}
                    alt={data.title}
                    onError={(ev) => ev.currentTarget.src = 'https://cdn.dresden.vision/images/noimage.png'}
                  />
                ) : (
                  <LoadingImage
                    className={cx(
                      { 'scale-90': (router.asPath.indexOf('glasses') > -1 || router.asPath.indexOf('frames') > -1) || isShopPage }
                    )}
                    quality={90}
                    style={{
                      objectFit: "cover",
                    }}
                    sizes="414px"
                    src={cardImages[0]}
                    alt={data.title}
                    priority={data.prio}
                  />
                ))}
              </div>
            </div>
          ) : (
            <ProductCardMobileSwiper cardImages={cardImages} title={data.title} />
          )}
        </div>

        {/* Product info container */}
        <div className="flex flex-col flex-grow p-2">
          {/* Title and price with Try me on button */}
          <div>
            <div className="flex items-center gap-2">
              <div className="flex-1 flex items-center gap-2">
                <h3 className={cx(
                  "text-[16px] text-brand-black100 overflow-hidden text-ellipsis",
                  {
                    'text-left': vtoEnabled,
                  }
                )}>
                  {(data.shape ? frameShapeToName(data.shape) + ' - ' : '') + data.title}
                </h3>
                <span className="font-medium">
                  <PriceBadge price={calculatedPrice} regularPrice={calculatedRegularPrice} />
                </span>
              </div>
              {(!!vtoEnabled && !isSunglassess) && (
                <div className="">
                  <ProductCardVTOToggleButton
                    onToggle={() => {
                      toggleVTO?.(selectedLensColor?.id || 'grey', data.armColorSlug || data.colorSlug || '', data.shape || 'classic');
                      ga.sendUICustomEvent({
                        eventCategory: 'VTO',
                        eventAction: 'VTO Toggle Click',
                        eventLabel: 'Category Page'
                      });
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Bottom actions */}
          <div className={cx(
            'flex items-center justify-between gap-2 mt-[16px]',
            { 'mt-auto': !isSunglassess }
          )}>
            {isSunglassess && (
              <LensColorSelector
                title="Lens Colour"
                colors={filteredLensColors}
                selectedColor={selectedLensColor}
                updateSelectedColor={(colorId: string) => {
                  setSelectedLensColor(() => getColorMap(colorId as string))
                }}
                onHover={handleLensColorHover}
              />
            )}
            {(!!vtoEnabled && isSunglassess) && (
              <div className="flex-shrink-0">
                <ProductCardVTOToggleButton
                  onToggle={() => {
                    toggleVTO?.(selectedLensColor?.id || 'grey', data.armColorSlug || data.colorSlug || '', data.shape || 'classic');
                    ga.sendUICustomEvent({
                      eventCategory: 'VTO',
                      eventAction: 'VTO Toggle Click',
                      eventLabel: 'Category Page'
                    });
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}

const ProductCardVTOToggleButton: FC<{ onToggle: () => void }> = ({
  onToggle
}) => {
  return (
    <UIButton
      light
      customFontSize={true}
      className="text-[13px] !w-[95px] !h-[40px] !px-[0px]"
      onClick={(e) => {
        e.preventDefault();
        onToggle();
      }}
    >
      Try me on
    </UIButton>
  );
};

export default React.memo(ProductCard);